import { inject, Injectable, signal } from '@angular/core';

import { map, switchMap, take } from 'rxjs';

import { TranslocoService } from '@jsverse/transloco';

import { AlertInputs, AlertPosition } from './alert.inputs';
import { Alert } from './alert.model';

type HashMap<T = unknown> = Record<string, T>;
type AlertOptions = { duration?: number; params?: HashMap; position?: AlertPosition };
const defaultAlertOptions = { duration: Infinity };
const MAX_TOAST_TIMEOUT = 2147483647;

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private readonly translocoService = inject(TranslocoService);
  private readonly alertsState = signal<Alert[]>([]);

  readonly alerts = this.alertsState.asReadonly();

  addAlert(alert: Alert) {
    this.alertsState.update((alerts) => [...alerts, alert]);
  }

  removeAlert(alert: Alert) {
    this.alertsState.update((alerts) => alerts.filter((currentAlert) => currentAlert !== alert));
  }

  info(title: string, description: string, options: AlertOptions = defaultAlertOptions) {
    const duration = this.getDuration(options);
    this.createAlert(title, description, {
      inputs: this.createToastOptions('info', duration, this.getPosition(options)),
      params: options.params,
    })
      .pipe(map((alert) => this.handlerAlert(alert)))
      .subscribe();
  }

  private getPosition(options: AlertOptions) {
    const paramsPosition = (options.params as { position?: AlertPosition } | undefined)?.position;
    return options.position ?? paramsPosition ?? 'top-right';
  }

  private handlerAlert(alert: Alert) {
    this.addAlert(alert);
    return alert;
  }

  success(title: string, description: string, options: AlertOptions = defaultAlertOptions) {
    const duration = this.getDuration(options);
    this.createAlert(title, description, {
      inputs: this.createToastOptions('success', duration, this.getPosition(options)),
      params: options.params,
    })
      .pipe(map((alert) => this.handlerAlert(alert)))
      .subscribe();
  }

  error(title: string, description: string, options: AlertOptions = defaultAlertOptions) {
    const duration = this.getDuration(options);
    this.createAlert(title, description, {
      inputs: this.createToastOptions('error', duration, this.getPosition(options)),
      params: options.params,
    })
      .pipe(map((alert) => this.handlerAlert(alert)))
      .subscribe();
  }

  warning(title: string, description: string, options: AlertOptions = defaultAlertOptions) {
    const duration = this.getDuration(options);
    this.createAlert(title, description, {
      inputs: this.createToastOptions('warning', duration, this.getPosition(options)),
      params: options.params,
    })
      .pipe(map((alert) => this.handlerAlert(alert)))
      .subscribe();
  }

  private getDuration(options: AlertOptions) {
    return options.duration ?? defaultAlertOptions.duration;
  }

  private createToastOptions(status: AlertInputs['status'], duration: number, position: AlertPosition) {
    return {
      status,
      position,
      selfClosingTimeout: this.getToastTimeout(duration),
    };
  }

  private getToastTimeout(duration: number) {
    return Number.isFinite(duration) ? duration : MAX_TOAST_TIMEOUT;
  }

  createAlert(
    title: string,
    description: string,
    options: { inputs: Omit<Partial<AlertInputs>, 'header' | 'description'>; params?: HashMap },
  ) {
    return this.getTitleAndDescription(title, description, options.params).pipe(
      map(({ header, description }) => new Alert({ ...options.inputs, header, description })),
    );
  }

  private getTitleAndDescription(title: string, description: string, params?: HashMap) {
    return this.getMessage(title, params).pipe(
      switchMap((header: string) =>
        this.getMessage(description, params).pipe(map((message: string) => ({ header, description: message }))),
      ),
    );
  }

  private getMessage(text: string, params?: HashMap) {
    return this.translocoService.selectTranslate(text, params).pipe(take(1));
  }
}
